// EntityScreen — the canonical entity-browser pane primitive.
//
// Per CLAUDE.md Iron Rule 11, every entity browser in Eidollon (Horizon,
// Terminal closing-soon pane, Intel feed, Ops case board, Audit log search,
// Vendor browser, Agency browser, Award browser) renders one of these.
//
// Composition (top-to-bottom inside the page canvas):
//
//   ┌─ IntentBar (32) ────────────────────────────────────────────────┐
//   ├─ FilterBar (28, optional, when rail collapsed) ─────────────────┤
//   ├─ Rail 232 │ Result body (TopSlot + VirtualTable)        │ AI 320│
//   ├─ ResultMetaBar (26) ────────────────────────────────────────────┘
//
// All state is owned by the host (URL search params, TanStack Query, etc.).
// EntityScreen is a controlled view layer.

import type { filters as f, search } from '@eidollon/contracts';
import { FacetPanel, type FacetPanelProps } from '@eidollon/ui-filters';
import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { IntentBar, type IntentBarProps } from './IntentBar.js';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from './Resizable.js';
import { ResultMetaBar, type ResultMetaBarProps } from './ResultMetaBar.js';
import { type RailMode, SuggestionRail } from './SuggestionRail.js';
import { type ColumnDef, type SortState, VirtualTable } from './VirtualTable.js';
import { useIsMobile } from './useBreakpoint.js';

// Versioned persistence key for the filter-rail / result-column split.
// Bumping :vN here is a key rename, not a data migration — old layouts are
// simply ignored and the next drag re-creates them under the new key.
//
// v1 → v2 (2026-05-13): v1 layouts were corrupted by a unit-interpretation
// bug in our Resizable wrapper — `maxSize={35}` was forwarded as the number
// 35, which react-resizable-panels v4 reads as 35 PIXELS (not 35%). The
// resulting persisted layout pinned the rail at ~3.3% of the group, which is
// unreadable. The wrapper now stringifies sizes to `<n>%`; this key bump
// invalidates the bad v1 cache so users get the corrected default once.
const ENTITY_SCREEN_LAYOUT_KEY = 'eid:shell:panes:v2.entityscreen';

export interface EntityScreenProps<R> {
  /** Entity / filter contract. */
  schema: f.FilterSchema;

  /** Current filter state + commit handler. Owner persists to URL. */
  state: f.FilterState;
  onStateChange: (next: f.FilterState) => void;

  /** Sort + commit. */
  sort?: SortState;
  onSortChange?: (next: SortState) => void;

  /** Loaded rows. */
  rows: R[];
  rowKey: (row: R) => string;
  columns: ColumnDef<R>[];
  onRowClick?: (row: R) => void;
  rowTone?: (row: R) => 'selected' | 'watch' | undefined;

  /** Counts for IntentBar / meta. */
  count: number | null;
  total?: number | null;
  loading?: boolean;
  hasMore?: boolean;

  /** AI Rail wiring. Pass `ai={null}` to disable. */
  ai: {
    suggestions: search.Suggestion[];
    coldStart?: boolean;
    onOpen: (s: search.Suggestion) => void;
    onDismiss: (s: search.Suggestion) => void;
    onMuteCategory: (k: search.SuggestionKind) => void;
    initialMode?: RailMode;
  } | null;

  /** Facet bucket counts (host fetches from /aggregations). */
  facetCounts?: FacetPanelProps['counts'];
  asyncFetchers?: FacetPanelProps['asyncFetchers'];
  resolveAsyncLabel?: FacetPanelProps['resolveAsyncLabel'];
  rangePresets?: FacetPanelProps['rangePresets'];
  geoEditors?: FacetPanelProps['geoEditors'];

  /** NL composer wiring. Editing state lives in the host so URL stays canonical. */
  nlEditing?: boolean;
  nlValue?: string;
  onNlEditChange?: (v: string) => void;
  onNlSubmit?: (text: string) => void;
  onActivateNl?: () => void;
  onCancelNl?: () => void;

  /** Leftover free-text from the most recent NL rewrite (the part the model
   *  couldn't map to a structured filter). Renders as an "ignored" chip in
   *  the IntentBar with promote/drop affordances. */
  nlLeftover?: string | null;
  onPromoteNlLeftover?: (text: string) => void;
  onDropNlLeftover?: () => void;

  /** Slot rendered in the IntentBar's right region, immediately before the
   *  result-count + AI badge. Hosts use this to mount the platform
   *  `SavedView` primitive on every entity browser (Iron Rule #11). Intel
   *  routes its SavedView through `IntelViewTabs.rightSlot` because that
   *  surface owns a tab strip; the rest of the entity browsers consume this
   *  slot instead. */
  headerRightSlot?: ReactNode;

  /** Optional slot above the table (e.g. Horizon's "Top 5 today"). */
  topSlot?: ReactNode;

  /** Optional pagination controls rendered in the meta footer's right slot.
   *  Hosts pass a `<Pagination …>` configured against URL state. The page is
   *  the host's responsibility (the platform stays controlled). When provided
   *  it overrides `meta.rightSlot` (callers needing both should compose them
   *  in a single node). */
  pagination?: ReactNode;

  /**
   * How `topSlot` shares vertical space with the result table.
   *
   *   - `"fixed"` (default) — slot takes the height of its content; the
   *     table fills the rest. Use for thin headers (Horizon "Top 5", a
   *     summary tile, an alert tape).
   *   - `"grow"` — slot fills the result column; the table is suppressed.
   *     Use when the slot owns the entire body (e.g. Intel renders its
   *     own MAP / TIMELINE / ANALYTICS tabs in topSlot — the platform
   *     table doesn't apply).
   */
  topSlotMode?: 'fixed' | 'grow';

  /** Empty-state node (rendered inside VirtualTable). */
  emptyState?: ReactNode;

  /** Meta bar overrides (sort label, backend, latency, fromCache). */
  meta?: Pick<
    ResultMetaBarProps,
    'sortLabel' | 'backend' | 'latencyMs' | 'fromCache' | 'rightSlot'
  >;

  /** i18n hooks. Hosts wire react-i18next here; the platform stays i18n-free. */
  intentLabels?: IntentBarProps['labels'];
  formatFieldLabel?: (fieldId: string, fallback: string) => string;
  formatOptionLabel?: (fieldId: string, value: string) => string | undefined;
  facetChrome?: FacetPanelProps['facetChrome'];

  /** Optional node rendered ABOVE the FacetPanel inside the filter rail.
   *  Used by surfaces that need a domain-tree picker (intel: domain →
   *  subcategory) without forking the rail — see Iron Rule #11. */
  railTopSlot?: ReactNode;
}

export function EntityScreen<R>({
  schema,
  state,
  onStateChange,
  sort,
  onSortChange,
  rows,
  rowKey,
  columns,
  onRowClick,
  rowTone,
  count,
  total,
  loading,
  hasMore,
  ai,
  facetCounts,
  asyncFetchers,
  resolveAsyncLabel,
  rangePresets,
  geoEditors,
  nlEditing,
  nlValue,
  onNlEditChange,
  onNlSubmit,
  onActivateNl,
  onCancelNl,
  nlLeftover,
  onPromoteNlLeftover,
  onDropNlLeftover,
  headerRightSlot,
  topSlot,
  topSlotMode = 'fixed',
  emptyState,
  meta,
  pagination,
  intentLabels,
  formatFieldLabel,
  formatOptionLabel,
  facetChrome,
  railTopSlot,
}: EntityScreenProps<R>) {
  const [railMode, setRailMode] = useState<RailMode>(ai?.initialMode ?? 'pinned');
  const isMobile = useIsMobile();
  // Mobile only: the filter rail is a toggled overlay drawer, not a permanent
  // column. Closed by default so results own the viewport.
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const filterRail = (
    <>
      {railTopSlot}
      <FacetPanel
        schema={schema}
        state={state}
        onChange={onStateChange}
        counts={facetCounts}
        asyncFetchers={asyncFetchers}
        resolveAsyncLabel={resolveAsyncLabel}
        rangePresets={rangePresets}
        geoEditors={geoEditors}
        formatFieldLabel={formatFieldLabel}
        formatOptionLabel={formatOptionLabel}
        facetChrome={facetChrome}
      />
    </>
  );

  const resultColumn = (
    <>
      {topSlot && (
        <div
          style={
            topSlotMode === 'grow'
              ? {
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }
              : { flexShrink: 0 }
          }
        >
          {topSlot}
        </div>
      )}
      {topSlotMode !== 'grow' && (
        <VirtualTable
          rows={rows}
          columns={columns}
          rowKey={rowKey}
          onRowClick={onRowClick}
          sort={sort}
          onSortChange={onSortChange}
          rowTone={rowTone}
          emptyState={emptyState}
        />
      )}
    </>
  );

  return (
    <div
      data-component="EntityScreen"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {loading && <div className="progress" />}

      <IntentBar
        schema={schema}
        state={state}
        onChange={onStateChange}
        count={count}
        total={total}
        aiBadge={
          ai && ai.suggestions.length > 0
            ? {
                count: ai.suggestions.length,
                tone: 'amber',
                onClick: () => setRailMode(railMode === 'collapsed' ? 'pinned' : 'expanded'),
              }
            : null
        }
        editing={nlEditing}
        editValue={nlValue}
        onEditChange={onNlEditChange}
        onSubmitNl={onNlSubmit}
        onActivateEdit={onActivateNl}
        onCancelEdit={onCancelNl}
        leftoverQ={nlLeftover ?? null}
        onPromoteLeftover={onPromoteNlLeftover}
        onDropLeftover={onDropNlLeftover}
        rightSlot={headerRightSlot}
        labels={intentLabels}
        formatFieldLabel={formatFieldLabel}
        formatOptionLabel={formatOptionLabel}
      />

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Desktop: filter rail vs result-column split — resizable + persisted
            under a versioned shell key. The AI rail (when present) stays
            OUTSIDE the resizable group because it has its own
            collapse/pin/expanded tri-state and a fixed expanded width; mixing
            those concerns is confusing for users. Defaults: rail 18%, body
            82%. Drag bounds keep both panes usable: rail min 12% (~150px at
            1280px wide), max 35%; body min 50%.

            Mobile (≤768px): the resizable split can't work on a phone — the
            rail would eat the viewport and the result panel's internal scroll
            collapses inside the shell's overflow:hidden. So results take the
            full width and the rail moves behind a drawer (below). */}
        {isMobile ? (
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {resultColumn}
          </div>
        ) : (
          <ResizablePanelGroup direction="horizontal" autoSaveId={ENTITY_SCREEN_LAYOUT_KEY}>
            <ResizablePanel
              id="filter-rail"
              defaultSize={18}
              minSize={12}
              maxSize={35}
              style={{
                background: 'var(--bg-pane)',
                borderRight: '1px solid var(--bd)',
                overflow: 'auto',
              }}
            >
              {filterRail}
            </ResizablePanel>

            <ResizableHandle aria-label="Resize filter rail" />

            <ResizablePanel id="result-column" defaultSize={82} minSize={50}>
              {resultColumn}
            </ResizablePanel>
          </ResizablePanelGroup>
        )}

        {/* AI Rail — mobile-aware internally (desktop side rail; mobile bottom
            sheet + floating pill). */}
        {ai && (
          <SuggestionRail
            mode={railMode}
            onModeChange={setRailMode}
            suggestions={ai.suggestions}
            onOpen={ai.onOpen}
            onDismiss={ai.onDismiss}
            onMuteCategory={ai.onMuteCategory}
            loading={loading}
            coldStart={ai.coldStart}
          />
        )}

        {/* Mobile filter drawer + trigger. Filters live behind a button so the
            result list owns the viewport; the drawer slides over from the left
            (tap the backdrop or × to dismiss). */}
        {isMobile && (
          <>
            <button
              type="button"
              onClick={() => setFilterDrawerOpen(true)}
              style={filtersFabStyle}
              aria-label="open filters"
            >
              ☰ FILTERS
            </button>
            {filterDrawerOpen && (
              <>
                <div
                  aria-hidden="true"
                  onClick={() => setFilterDrawerOpen(false)}
                  style={drawerBackdropStyle}
                />
                <div style={filterDrawerStyle}>
                  <header style={filterDrawerHeaderStyle}>
                    <span
                      style={{
                        fontFamily: 'var(--mono)',
                        fontSize: 11,
                        letterSpacing: 0.4,
                        color: 'var(--fg-3)',
                      }}
                    >
                      FILTERS
                    </span>
                    <button
                      type="button"
                      onClick={() => setFilterDrawerOpen(false)}
                      aria-label="close filters"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--fg-2)',
                        fontSize: 18,
                        lineHeight: 1,
                        cursor: 'pointer',
                      }}
                    >
                      ×
                    </button>
                  </header>
                  <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{filterRail}</div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <ResultMetaBar
        count={count}
        total={total}
        sortLabel={meta?.sortLabel ?? (sort ? `${sort.field}:${sort.dir}` : undefined)}
        backend={meta?.backend}
        latencyMs={meta?.latencyMs}
        fromCache={meta?.fromCache}
        hasMore={hasMore}
        rightSlot={meta?.rightSlot}
      />

      {/* Pagination footer — own row, centered, full-width so it's the
          obvious affordance the operator reaches for. The meta bar above
          stays terse (counts + backend); paging lives down here. */}
      {pagination != null && (
        <div
          data-component="EntityScreen.PaginationFooter"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 8,
            padding: '10px 12px',
            borderTop: '1px solid var(--bd)',
            background: 'var(--bg-pane)',
            // Prominent enough that the operator notices, not so loud it
            // competes with row data.
            fontSize: 12,
            color: 'var(--fg-1)',
          }}
        >
          {pagination}
        </div>
      )}
    </div>
  );
}

// ── Mobile filter drawer + trigger styles ─────────────────────────────────
const filtersFabStyle: CSSProperties = {
  position: 'fixed',
  left: 12,
  // sit above the 52px bottom tab bar + iOS home indicator
  bottom: 'calc(52px + env(safe-area-inset-bottom, 0px) + 12px)',
  zIndex: 25,
  height: 36,
  padding: '0 14px',
  borderRadius: 18,
  background: 'var(--bg-pane-2, var(--bg-pane))',
  border: '1px solid var(--bd-strong, var(--bd))',
  color: 'var(--fg-2)',
  fontFamily: 'var(--mono)',
  fontSize: 11,
  letterSpacing: 0.4,
  cursor: 'pointer',
  boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
};

const drawerBackdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  zIndex: 40,
};

const filterDrawerStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  bottom: 0,
  zIndex: 41,
  width: 'min(86vw, 360px)',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  background: 'var(--bg-pane)',
  borderRight: '1px solid var(--bd-strong, var(--bd))',
  paddingTop: 'env(safe-area-inset-top, 0px)',
  boxShadow: '4px 0 24px rgba(0,0,0,0.4)',
};

const filterDrawerHeaderStyle: CSSProperties = {
  height: 44,
  padding: '0 14px',
  borderBottom: '1px solid var(--bd)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
};
